'use client';

import { useGSAP } from '@gsap/react';
import { FC, useRef } from 'react';

import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import { PartnershipHeroDto } from '@/components/Partnership/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { breadcrumbTrails } from '@/lib/breadcrumbs';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	data: PartnershipHeroDto;
}

const PartnershipHero: FC<Props> = ({ data }) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const breadcrumbsRef = useRef<HTMLDivElement>(null);

	useViewportVideosPlayback([wrapperRef]);

	useGSAP(
		() => {
			const wrapper = wrapperRef.current;
			const video = videoRef.current;

			if (!wrapper || !video) return;

			const titleLines = Array.from(wrapper.querySelectorAll<HTMLElement>(`.${css.title} p`));
			const tagEls = Array.from(wrapper.querySelectorAll<HTMLElement>(`.${css.tag}`));
			const breadcrumbs = breadcrumbsRef.current;

			const contentFrom: gsap.TweenVars = {
				opacity: 0,
				y: 40,
				filter: 'blur(10px)'
			};
			const contentTo: gsap.TweenVars = {
				opacity: 1,
				y: 0,
				filter: 'blur(0px)'
			};

			const blurEls = titleLines.filter(Boolean);

			gsap.set(video, { opacity: 0, scale: 1.1 });
			if (blurEls.length) gsap.set(blurEls, contentFrom);
			if (tagEls.length) gsap.set(tagEls, contentFrom);
			if (breadcrumbs) gsap.set(breadcrumbs, contentFrom);

			const tl = gsap.timeline({
				defaults: { ease: 'power3.out', duration: 1.2 },
				scrollTrigger: {
					trigger: wrapper,
					start: 'top 80%',
					toggleActions: 'play none none reverse'
				}
			});

			tl.to(video, {
				opacity: 1,
				scale: 1,
				duration: 1.8,
				ease: 'power2.out'
			});

			if (titleLines.length)
				tl.to(
					titleLines,
					{
						...contentTo,
						stagger: 0.15,
						duration: 1.1,
						ease: 'power3.out'
					},
					'-=1'
				);

			if (tagEls.length)
				tl.to(
					tagEls,
					{
						...contentTo,
						duration: 1,
						ease: 'power2.out',
						stagger: 0.08
					},
					'-=0.6'
				);

			if (breadcrumbs)
				tl.to(
					breadcrumbs,
					{
						...contentTo,
						duration: 1,
						ease: 'power2.out'
					},
					'-=0.6'
				);
		},
		{ scope: wrapperRef }
	);

	return (
		<section ref={wrapperRef} className={css.wrapper}>
			<div className={css.content}>
				<PageBreadcrumbs
					ref={breadcrumbsRef}
					items={breadcrumbTrails.partnership()}
					wrapperClassName={css.breadcrumbsWrap}
					className={css.breadcrumbs}
				/>
				<Typography className={css.title} variant="h2" tag="h1">
					<StrapiRichContent content={data.title} />
				</Typography>
				{data.tags?.length ? (
					<div className={css.tagsContainer}>
						{data.tags.map(tag => (
							<div key={tag.id} className={css.tag}>
								<StrapiRichContent content={tag.text} />
							</div>
						))}
					</div>
				) : null}

				<video
					ref={videoRef}
					autoPlay
					loop
					playsInline
					muted
					controls={false}
					className={css.video}
					preload="none"
					poster="/assets/images/partnership/partner-hero.webp"
				>
					<source
						src="/assets/video/mov/qream-pillow-letters.mov"
						type='video/quicktime; codecs="hvc1"'
					/>
					<source src="/assets/video/webm/qream-pillow-letters.webm" type="video/webm" />
				</video>
			</div>
		</section>
	);
};

export default PartnershipHero;
